import React, { createContext, useState, useContext, useEffect } from 'react';
import lightTheme from '../themes/light';
import darkTheme from '../themes/dark';
import barbieTheme from '../themes/barbie';
import smurfTheme from '../themes/smurf';
import shrekTheme from '../themes/shrek';
import { fetchUserPreferences, saveUserPreferences } from '../api/user-preferences-api';

// Import all language files dynamically
// All language files must be in "./language" directory and have ".js" extension
const req = require.context('../language', false, /\.js$/);
const translations = req.keys().reduce((acc, path) => {
  const langKey = path.replace('./', '').replace('.js', '').toUpperCase();
  acc[langKey] = req(path).default;
  return acc;
}, {});

const PreferenceContext = createContext();

const themes = { 
  light: lightTheme, 
  dark: darkTheme, 
  barbie: barbieTheme, 
  smurf: smurfTheme,
  shrek: shrekTheme 
};

// Helper to wrap translations with a Proxy for fallback
function createTranslationProxy(translationsObj, lang) {
  return new Proxy(translationsObj, {
    get(target, prop) {
      if (typeof prop === 'string') {
        return prop in target ? target[prop] : "language." + lang.toLowerCase() + "." + prop;
      }
      return target[prop];
    }
  });
}

// This component provides the preference context to its children
// The default language is set to 'EN', unless a user preference is found 
export const PreferenceProvider = ({ children, userId }) => {
  const [theme, setTheme] = useState('light');
  const [lang, setLang] = useState('EN');
  const [t, setT] = useState(createTranslationProxy(translations['EN'], 'EN'));

  // Load all preferences once on mount or when userId changes
  useEffect(() => {
    if (!userId) return;
  
    fetchUserPreferences(userId)
      .then(prefs => {
        if (prefs.theme && themes[prefs.theme] && prefs.theme !== theme) {
          setTheme(prefs.theme);
          if(prefs.theme === 'dark') {
            document.documentElement.style.colorScheme = 'dark';
          }
          else {
            document.documentElement.style.colorScheme = 'light';
          }
        }
        if (prefs.language && translations[prefs.language] && prefs.language !== lang) {
          setLang(prefs.language);
        }
      })
      .catch(console.error);
  }, [userId]);
  
  

  useEffect(() => {
    if (translations[lang]) setT(createTranslationProxy(translations[lang], lang));
  }, [lang]);

  useEffect(() => {
    const root = document.documentElement;
    const currentTheme = themes[theme] || themes.light;
    Object.entries(currentTheme).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });
  }, [theme]);
  

  const changeLanguage = async (newLang) => {
    if (!translations[newLang]) return;
  
    setLang(newLang);
  
    if (!userId) return;
  
    try {
      await saveUserPreferences(userId, { theme, language: newLang });
      const prefs = await fetchUserPreferences(userId);
      if (prefs.theme && themes[prefs.theme]) setTheme(prefs.theme);
      if (prefs.language && translations[prefs.language]) setLang(prefs.language);
    } catch (err) {
      console.error('Failed to save or reload preferences:', err);
    }
  };
  
  
  const changeTheme = async (newTheme) => {
    if (!themes[newTheme]) return;
  
    setTheme(newTheme);
  
    if (!userId) return;
  
    try {
      await saveUserPreferences(userId, { theme: newTheme, language: lang });
      const prefs = await fetchUserPreferences(userId);
      if (prefs.theme && themes[prefs.theme]) setTheme(prefs.theme);
      if (prefs.language && translations[prefs.language]) setLang(prefs.language);
    } catch (err) {
      console.error('Failed to save or reload preferences:', err);
    }
  };
  
  

  const getAvailableLanguages = () => Object.keys(translations);

  return (
    <PreferenceContext.Provider
      value={{
        theme,
        setTheme: changeTheme,
        lang,
        t,
        changeLanguage,
        getAvailableLanguages,
      }}
    >
      {children}
    </PreferenceContext.Provider>
  );
};

export const usePreferences = () => useContext(PreferenceContext);

export const useLanguage = () => {
  const { lang, t, changeLanguage, getAvailableLanguages } = usePreferences();
  return { lang, t, changeLanguage, getAvailableLanguages };
};
export const useTheme = () => {
  const { theme, setTheme } = usePreferences();
  return { theme, setTheme };
};
